import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ShieldAlert, ShieldCheck, Lock, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * Risk Settings — every value shown here maps 1:1 to a field in
 * `/api/risk/prefs`. Free tier can only flip the kill switch; every numeric
 * field is locked and shows a "Pro" pill. Pro can adjust each up to the
 * Pro-tier ceiling returned by GET /risk/prefs.
 */
export default function RiskSettings() {
  const [prefs, setPrefs] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [proMax, setProMax] = useState(null);
  const [tier, setTier] = useState("free");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/risk/prefs");
    setPrefs(data.prefs);
    setDefaults(data.defaults);
    setProMax(data.pro_max);
    if (data.tier) setTier(data.tier);
  };

  useEffect(() => { load(); }, []);

  const toggleKill = async () => {
    const next = !prefs.kill_switch;
    try {
      await api.post(`/risk/kill-switch?enabled=${next}`);
      setPrefs((p) => ({ ...p, kill_switch: next }));
      toast[next ? "warning" : "success"](
        next ? "Kill switch ON — all bots halted" : "Kill switch OFF — bots may resume",
        { description: next ? "No order will execute until you turn this off." : undefined }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't toggle kill switch");
    }
  };

  const savePref = async (key, value) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/risk/prefs", { [key]: value });
      setPrefs(data.prefs);
      setTier(data.tier);
      toast.success(`${labelOf(key)} updated`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("Free tier")) {
        setTier("free");
        toast.error("Free tier — upgrade to Pro to adjust numeric risk limits");
      } else {
        toast.error(detail || "Couldn't save");
      }
    }
    setSaving(false);
  };

  if (!prefs || !defaults || !proMax) {
    return <div className="text-muted-foreground text-sm">Loading risk settings…</div>;
  }

  const isFree = tier === "free";
  const isOwner = tier === "owner";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="risk-settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          Guardrails
          {isOwner && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono-tab uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                  data-testid="owner-badge">
              Owner · unlimited
            </span>
          )}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Risk settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isOwner
            ? "Owner access — position size, daily loss cap, and rate limits are all uncapped for your account. The global kill switch still works if you need to freeze everything."
            : "Every order — bot or manual, paper or live — passes through these limits. They can't be turned off, only tightened or (on Pro) loosened within safe caps."}
        </p>
      </div>

      {/* Kill switch — hero card, available on both tiers */}
      <Card className={`p-5 rounded-2xl border-2 transition-colors ${
        prefs.kill_switch ? "border-red-500/60 bg-red-500/[0.06]" : "border-border"
      }`} data-testid="kill-switch-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex items-start gap-3">
            {prefs.kill_switch
              ? <ShieldAlert className="w-6 h-6 text-red-400 mt-0.5" />
              : <ShieldCheck className="w-6 h-6 text-emerald-400 mt-0.5" />}
            <div>
              <div className="font-display text-lg font-bold">Global kill switch</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                One toggle to freeze every bot and reject every new order across your entire account.
                Existing positions are untouched.
              </div>
            </div>
          </div>
          <Button
            onClick={toggleKill}
            data-testid="kill-switch-btn"
            className={`rounded-full font-bold ${
              prefs.kill_switch
                ? "bg-emerald-500 hover:bg-emerald-400 text-black"
                : "bg-red-500 hover:bg-red-400 text-white"
            }`}
          >
            {prefs.kill_switch ? "Turn OFF" : "STOP EVERYTHING"}
          </Button>
        </div>
      </Card>

      <Card className="p-5 rounded-2xl border-border">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="font-display text-lg font-semibold">Position & rate limits</div>
          <Badge className={`border-0 text-[10px] font-mono-tab uppercase tracking-wider ${
            isOwner ? "bg-emerald-500/15 text-emerald-400"
            : isFree ? "bg-secondary/60 text-muted-foreground"
            : "bg-emerald-500/15 text-emerald-400"
          }`} data-testid="risk-tier-badge">
            {isOwner ? "Owner · unlimited" : isFree ? "Free tier · fixed" : "Pro tier · adjustable"}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PrefRow
            testid="pref-max-position-pct"
            label="Max position size"
            help="No single symbol can exceed this % of account equity."
            unit="%"
            value={prefs.max_position_pct}
            free={defaults.max_position_pct}
            proMax={proMax.max_position_pct}
            isFree={isFree}
            saving={saving}
            onSave={(v) => savePref("max_position_pct", v)}
          />
          <PrefRow
            testid="pref-max-daily-loss-pct"
            label="Max daily loss"
            help="Halt all bots for the day if realized P&L drops this % of start-of-day equity."
            unit="%"
            value={prefs.max_daily_loss_pct}
            free={defaults.max_daily_loss_pct}
            proMax={proMax.max_daily_loss_pct}
            isFree={isFree}
            saving={saving}
            onSave={(v) => savePref("max_daily_loss_pct", v)}
          />
          <PrefRow
            testid="pref-max-open-positions-per-bot"
            label="Max open positions per bot"
            help="A single bot won't open more concurrent positions than this."
            unit=""
            value={prefs.max_open_positions_per_bot}
            free={defaults.max_open_positions_per_bot}
            proMax={proMax.max_open_positions_per_bot}
            isFree={isFree}
            saving={saving}
            integer
            onSave={(v) => savePref("max_open_positions_per_bot", v)}
          />
          <PrefRow
            testid="pref-max-orders-per-hour"
            label="Max orders / hour per bot"
            help="Rate cap per bot. Global cap is 200/hr across all your bots."
            unit=""
            value={prefs.max_orders_per_hour}
            free={defaults.max_orders_per_hour}
            proMax={proMax.max_orders_per_hour}
            isFree={isFree}
            saving={saving}
            integer
            onSave={(v) => savePref("max_orders_per_hour", v)}
          />
        </div>

        {isFree && (
          <div className="mt-5 p-3 rounded-xl bg-secondary/40 border border-border/40 text-xs text-muted-foreground flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              Free tier keeps these fixed. Upgrade to <span className="text-foreground font-semibold">Pro</span> to
              adjust each limit within safe ceilings ({proMax.max_position_pct}% / {proMax.max_daily_loss_pct}% /
              {" "}{proMax.max_open_positions_per_bot} positions / {proMax.max_orders_per_hour} orders per hour).
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function labelOf(key) {
  return {
    max_position_pct: "Max position size",
    max_daily_loss_pct: "Max daily loss",
    max_open_positions_per_bot: "Max open positions per bot",
    max_orders_per_hour: "Max orders per hour",
  }[key] || key;
}

function PrefRow({ label, help, unit, value, free, proMax, isFree, integer, saving, onSave, testid }) {
  const [draft, setDraft] = useState(String(value ?? free ?? ""));
  useEffect(() => { setDraft(String(value ?? free ?? "")); }, [value, free]);
  const changed = String(draft) !== String(value);
  return (
    <div data-testid={testid}>
      <label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</label>
      <div className="text-[11px] text-muted-foreground/80 mt-0.5 mb-1.5 leading-tight">{help}</div>
      <div className="flex items-stretch gap-2">
        <div className="flex-1 relative">
          <Input
            type="number"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            step={integer ? 1 : 0.5}
            disabled={isFree || saving}
            className="font-mono-tab pr-8"
          />
          {unit && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              {unit}
            </span>
          )}
        </div>
        {!isFree && (
          <Button
            size="sm"
            variant={changed ? "default" : "outline"}
            disabled={!changed || saving}
            onClick={() => {
              const n = integer ? parseInt(draft, 10) : parseFloat(draft);
              if (!isFinite(n)) return toast.error("Enter a number");
              if (n > proMax) return toast.error(`Max allowed is ${proMax}${unit || ""}`);
              onSave(n);
            }}
            className="rounded-lg"
          >
            <Save className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground/60 mt-1 font-mono-tab">
        Free default: {free}{unit} · Pro max: {proMax}{unit}
      </div>
    </div>
  );
}
